/**
 * Task Card
 *
 * Displays a single onboarding task with status and action button.
 */

import { Icon, IconName } from '../Icon';
import { OnboardingTask, TaskId } from '../../stores/onboardingStore';

interface TaskCardProps {
  task: OnboardingTask;
  onAction: (taskId: TaskId) => void;
}

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

function getTaskIcon(taskId: TaskId): IconName {
  switch (taskId) {
    case 'create_character':
      return 'token';
    case 'set_avatar':
      return 'eye';
    case 'review_rules':
      return 'scroll';
    default:
      return 'check';
  }
}

function getActionLabel(taskId: TaskId): string {
  switch (taskId) {
    case 'create_character':
      return 'Create';
    case 'set_avatar':
      return 'Set';
    case 'review_rules':
      return 'Review';
    default:
      return 'Complete';
  }
}

export function TaskCard({ task, onAction }: TaskCardProps) {
  const icon = getTaskIcon(task.id);
  const actionLabel = getActionLabel(task.id);

  if (task.completed) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-sm)',
          padding: 'var(--spacing-sm)',
          backgroundColor: 'rgba(74, 222, 128, 0.05)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid rgba(74, 222, 128, 0.2)',
          opacity: 0.7,
        }}
      >
        <Icon name="check" size={16} color="#4ade80" />
        <span
          style={{
            flex: 1,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            textDecoration: 'line-through',
          }}
        >
          {task.title}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 'var(--spacing-md)',
        backgroundColor: 'var(--color-surface)',
        borderRadius: 'var(--radius-sm)',
        border: `1px solid ${GOLD_DIM}`,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-sm)',
          marginBottom: 'var(--spacing-xs)',
        }}
      >
        <Icon name={icon} size={16} color={GOLD} />
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: 'var(--color-text)',
          }}
        >
          {task.title}
        </span>
      </div>

      {/* Description */}
      <p
        style={{
          margin: 0,
          marginBottom: 'var(--spacing-sm)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          lineHeight: 1.4,
        }}
      >
        {task.description}
      </p>

      {/* Action button */}
      <button
        onClick={() => onAction(task.id)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--spacing-xs)',
          padding: 'var(--spacing-xs) var(--spacing-sm)',
          background: `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`,
          border: `1px solid ${GOLD}`,
          borderRadius: 'var(--radius-sm)',
          color: '#1a1410',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          cursor: 'pointer',
        }}
      >
        <Icon name="plus" size={12} />
        {actionLabel}
      </button>
    </div>
  );
}
